import React, { ReactNode } from "react";
import { Box } from "@mui/system";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import theme from "../../../theme";
import { TimelineOppositeContent } from "@mui/lab";

export type Step = {
  hideFirstLine?: boolean;
  hideLastLine?: boolean;
  content?: ReactNode;
  contentAbove?: ReactNode;
  contentBelow?: ReactNode;
  // Spread onto the horizontal content-below cell (e.g. data-testid hooks).
  contentBelowDataAttributes?: Record<string, string | undefined>;
  oppositeContent?: ReactNode;
  connectorLabel?: ReactNode;
  // Spread onto the connector label wrapper (e.g. data-testid hooks).
  connectorDataAttributes?: Record<string, string | undefined>;
  header?: ReactNode;
  color?: string;
  indicator?: ReactNode;
  onIndicatorClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
  separatorLength?: number;
  id: string;
  // Spread onto the step's <li> (e.g. data-testid hooks).
  dataAttributes?: Record<string, string | undefined>;
};

type StepIndicatorProps = {
  steps: Step[];
  horizontal?: boolean;
  fullWidth?: boolean;
};

const connectorSx = (
  hide: boolean | undefined,
  separatorLength?: number,
  isFlexGrow?: boolean,
) => ({
  borderColor: hide ? "transparent" : theme.palette.primary.main,
  borderStyle: "dashed",
  bgcolor: "transparent",
  borderWidth: 0,
  flexShrink: 0,
  flex: hide ? "0 0 auto" : isFlexGrow ? 1 : 0,
  minHeight: hide ? 0 : separatorLength ?? 40,
  height: hide ? 0 : undefined,
  borderLeftWidth: hide ? 0 : 1,
});

function HorizontalStepDot({
  id,
  color,
  indicator,
  onIndicatorClick,
}: Pick<Step, "id" | "color" | "indicator" | "onIndicatorClick">) {
  return (
    <Box
      data-testid={onIndicatorClick ? `step-indicator-${id}` : undefined}
      onClick={onIndicatorClick}
      role={onIndicatorClick ? "button" : undefined}
      sx={{
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: "white",
        borderColor: color,
        borderWidth: 2,
        borderStyle: "solid",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        cursor: onIndicatorClick ? "pointer" : "default",
        zIndex: 1,
      }}
    >
      {indicator || (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: color,
          }}
        />
      )}
    </Box>
  );
}

const STOP_COLUMN_FR = 1;
const GAP_COLUMN_FR = 0.6;
const STOP_COLUMN_PX = 180;
const GAP_COLUMN_PX = 100;

function getHorizontalRailEdgeInsetPercent(
  stepCount: number,
  fullWidth?: boolean,
): number {
  if (stepCount < 2) {
    return 0;
  }

  const stopSize = fullWidth ? STOP_COLUMN_FR : STOP_COLUMN_PX;
  const gapSize = fullWidth ? GAP_COLUMN_FR : GAP_COLUMN_PX;
  const totalSize = stepCount * stopSize + (stepCount - 1) * gapSize;

  return (stopSize / 2 / totalSize) * 100;
}

function HorizontalStepIndicator({
  steps,
  fullWidth,
}: {
  steps: Step[];
  fullWidth?: boolean;
}) {
  const columnTemplate = steps
    .map((_, index) => {
      const stopCol = fullWidth ? "minmax(140px, 1fr)" : "180px";
      if (index === 0) {
        return stopCol;
      }
      const gapCol = fullWidth ? "minmax(80px, 0.6fr)" : "100px";
      return `${gapCol} ${stopCol}`;
    })
    .join(" ");

  const stopGridColumn = (stepIndex: number) => stepIndex * 2 + 1;
  const gapGridColumn = (gapIndex: number) => gapIndex * 2 + 2;
  const railEdgeInsetPercent = getHorizontalRailEdgeInsetPercent(
    steps.length,
    fullWidth,
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: columnTemplate,
        gridTemplateRows: "auto 28px auto",
        width: fullWidth ? "100%" : "max-content",
        minWidth: "100%",
        alignItems: "stretch",
        overflow: "visible",
      }}
    >
      {steps.map((step, index) => {
        const aboveContent = step.contentAbove ?? step.content;
        return (
          <Box
            key={`${step.id}-above`}
            {...(step.dataAttributes || {})}
            sx={{
              gridColumn: stopGridColumn(index),
              gridRow: 1,
              px: 0.5,
              pb: 0.5,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {aboveContent}
          </Box>
        );
      })}

      {steps.length > 1 ? (
        <Box
          sx={{
            gridColumn: "1 / -1",
            gridRow: 2,
            position: "relative",
            height: 28,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: `${railEdgeInsetPercent}%`,
              right: `${railEdgeInsetPercent}%`,
              borderTopWidth: 1,
              borderTopStyle: "dashed",
              borderTopColor: theme.palette.primary.main,
            }}
          />
        </Box>
      ) : null}

      {steps.slice(0, -1).map((step, index) => (
        <Box
          key={`gap-label-${step.id}`}
          sx={{
            gridColumn: gapGridColumn(index),
            gridRow: 2,
            position: "relative",
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {step.connectorLabel ? (
            <Box
              {...(step.connectorDataAttributes || {})}
              sx={{
                px: 0.5,
                bgcolor: theme.palette.primaryBackground.main,
                lineHeight: 1.2,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {step.connectorLabel}
            </Box>
          ) : null}
        </Box>
      ))}

      {steps.map((step, index) => (
        <Box
          key={`${step.id}-rail`}
          sx={{
            gridColumn: stopGridColumn(index),
            gridRow: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 28,
            position: "relative",
            zIndex: 1,
          }}
        >
          <HorizontalStepDot
            id={step.id}
            color={step.color}
            indicator={step.indicator}
            onIndicatorClick={step.onIndicatorClick}
          />
        </Box>
      ))}

      {steps.map((step, index) => (
        <Box
          key={`${step.id}-below`}
          {...(step.contentBelowDataAttributes || {})}
          sx={{
            gridColumn: stopGridColumn(index),
            gridRow: 3,
            px: 0.5,
            pt: 0.5,
            textAlign: "center",
          }}
        >
          {step.contentBelow}
        </Box>
      ))}
    </Box>
  );
}

const StepIndicator = ({ steps, horizontal, fullWidth }: StepIndicatorProps) => {
  if (horizontal) {
    return <HorizontalStepIndicator steps={steps} fullWidth={fullWidth} />;
  }

  const showOppositeColumn = (steps || []).some(
    (step) => step.oppositeContent != null,
  );

  return (
    <Timeline
      sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
        "&": {
          margin: 0,
        },
      }}
    >
      {(steps || []).map((step) => {
        const {
          hideFirstLine,
          hideLastLine,
          content,
          contentAbove,
          oppositeContent,
          connectorLabel,
          connectorDataAttributes,
          color,
          indicator,
          onIndicatorClick,
          separatorLength,
        } = step;

        const aboveContent = contentAbove ?? content;

        const dot = (
          <TimelineDot
            onClick={onIndicatorClick}
            sx={{
              marginLeft: 0,
              marginRight: 0,
              marginTop: 0.5,
              marginBottom: 0.2,
              flexShrink: 0,
              cursor: onIndicatorClick ? "pointer" : "default",
              width: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: "white",
              borderColor: color,
              borderWidth: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 0,
            }}
          >
            {indicator || (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: color,
                }}
              />
            )}
          </TimelineDot>
        );

        return (
          <TimelineItem
            key={step.id}
            {...(step.dataAttributes || {})}
            sx={{ alignItems: "stretch" }}
          >
            {step.header}
            {showOppositeColumn ? (
              <TimelineOppositeContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: "0 0 auto",
                  minWidth: 110,
                  py: 0,
                }}
                color="text.secondary"
              >
                {oppositeContent}
              </TimelineOppositeContent>
            ) : null}
            <TimelineSeparator
              sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
              }}
            >
              <TimelineConnector
                sx={connectorSx(hideFirstLine, separatorLength, false)}
              />
              {dot}
              <TimelineConnector
                sx={connectorSx(hideLastLine, separatorLength, true)}
              />
            </TimelineSeparator>
            <TimelineContent
              sx={{
                display: "flex",
                alignItems: "flex-start",
                py: 0.5,
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {aboveContent}
                </Box>
                {connectorLabel ? (
                  <Box
                    {...(connectorDataAttributes || {})}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      mt: 0.25,
                      mb: 0.5,
                    }}
                  >
                    {connectorLabel}
                  </Box>
                ) : null}
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default StepIndicator;
